import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Presentation } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { PresentationCard } from '@/features/presentations/components/PresentationCard';
import { SlidePreview } from '@/features/presentations/components/SlidePreview';
import { CreateWizardDialog } from '@/features/presentations/components/CreateWizardDialog';

interface Slide {
  slide_number: number;
  type: 'title' | 'content' | 'two_column' | 'quote';
  title: string;
  bullets?: string[];
  left_content?: string;
  right_content?: string;
  quote?: string;
  attribution?: string;
}

interface PresentationItem {
  id: string;
  title: string;
  slide_count: number;
  status: 'generating' | 'completed' | 'failed';
  created_at: string;
}

export function PresentationsPage() {
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Fetch presentations
  const { data: presentations, isLoading } = useQuery({
    queryKey: ['presentations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('presentation')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PresentationItem[];
    },
  });

  // Delete presentation
  const deletePres = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('presentation')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, id) => {
      if (previewId === id) setPreviewId(null);
      queryClient.invalidateQueries({ queryKey: ['presentations'] });
    },
  });

  // Fetch slides for preview
  const { data: previewSlides } = useQuery({
    queryKey: ['presentation-slides', previewId],
    queryFn: async () => {
      if (!previewId) return [];
      const { data, error } = await supabase
        .from('presentation_slide')
        .select('*')
        .eq('presentation_id', previewId)
        .order('slide_number');
      if (error) throw error;
      return data as Slide[];
    },
    enabled: !!previewId,
  });

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      <PageHeader
        title="Presentations"
        description={`${presentations?.length ?? 0} presentations`}
        action={
          <Button size="sm" onClick={() => setWizardOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Create Presentation
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 pb-8">
        {/* Preview mode */}
        {previewId && previewSlides ? (
          <div className="space-y-4">
            <Button variant="outline" size="sm" onClick={() => setPreviewId(null)}>
              Back to grid
            </Button>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {previewSlides.map((slide) => (
                <SlidePreview key={slide.slide_number} slide={slide} />
              ))}
            </div>
          </div>
        ) : isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-lg" />
            ))}
          </div>
        ) : !presentations || presentations.length === 0 ? (
          <EmptyState
            icon={Presentation}
            title="No presentations"
            description="Create your first AI-powered presentation"
            actionLabel="Create Presentation"
            onAction={() => setWizardOpen(true)}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {presentations.map((pres) => (
              <PresentationCard
                key={pres.id}
                presentation={pres}
                onClick={() => setPreviewId(pres.id)}
                onDelete={(id) => deletePres.mutate(id)}
              />
            ))}
          </div>
        )}
      </div>

      <CreateWizardDialog open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}
